import React, { useContext, useState, useEffect } from 'react';
import {
  Link,
  NavLink,
  useLocation,
  // useMatch,
  // useNavigate,
} from 'react-router-dom';
import {
  Button,
  Container,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  Text,
  SlideFade,
  useColorMode,
} from '@chakra-ui/react';
import { HamburgerIcon, MoonIcon, SunIcon } from '@chakra-ui/icons';
import { colors, focusVisibleProps, media, spacing } from '../../../App/ChakraTheme';

import classnames from 'classnames';
import styled, { css } from '../../../consts/styled';
import ConditionalWrap from '../../../components/ContitionalWrap';

import { ReactComponent as Logo } from '../assets/img-stemi-logo.svg';
import MainSidebar from './MainSidebar';
import { AuthContext, Role } from '../../../context/AuthProvider';
import { DashboardPage } from '../../app';
import { SignInPage, SignOutPage } from '../../auth';
import SignUpPage from '../../auth/pages/SignUpPage';
import classNames from 'classnames';
import { showSettingsHoc } from '../../../helpers/showSettingsHoc';
import { Navigation } from '../../../components/Navigation';
import { useTranslation } from 'react-i18next';
import LanguagePicker from './LanguagePicker';

const navLinkStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  color: ${colors.applyOpacity(colors.black, 0.2)};

  &h5 {
    pointer-events: none;
  }

  h5 {
    color: ${colors.darkblue};
    text-align: center;
  }

  &:before {
    content: '';
    width: 100%;
    height: 5px;
    position: absolute;
    bottom: -9px;
    background-color: transparent;
    transition: background-color 400ms;
  }

  &.active {
    &:before {
      background-color: ${colors.darkblue};
    }
  }
  &:hover {
    &:before {
      background-color: ${colors.applyOpacity(colors.darkblue, 0.7)};
    }
  }
  &:focus {
    outline: none;
    box-shadow: none;
  }
  &:focus-visible {
    outline: 2px solid #3182ce;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const StyledNavLink = styled(NavLink)`
  ${navLinkStyle}
`;
const StyledNavA = styled.a`
  ${navLinkStyle}
`;

const NavigationMobileScroll = styled(Navigation)`
  position: relative;
  padding-top: 0;
  width: 100%;
  ${media.phone`
    &.mobileSticky {
      position: sticky;
      transform: translateY(-56px);
    }
    &.mobileSticky.show {
      transition: transform 0.4s ease-in 0.2s;
      transform: translateY(0px);
      padding-top: 0;
      background-color: ${colors.white};
      box-shadow: 0 1px ${colors.whitesmoke}, 0 -1px ${colors.whitesmoke};
    }
  `}
`;

const NavigationChildrenContainer = styled.div`
  padding-right: ${spacing.BASE_PLUS};
  padding-left: ${spacing.BASE_PLUS};
  &.marketplace {
    padding-left: 0px !important;
    ppadding-right: 0px !important;
    margin-left: 0px !important;
    margin-right: 0px !important;
  }
`;

const HeaderContainer = styled.header`
  background-color: rgb(238, 240, 248);
  padding-bottom: ${spacing.BASE};
  padding-top: ${spacing.SMALL};
  width: 100%;
  &.marketplace {
    background: white;
  }
`;

const MainNavigation: React.FC<{
  showSettings?: boolean;
  hideSecondRow?: boolean;
}> = (props) => {
  const { t } = useTranslation();
  const { colorMode, toggleColorMode } = useColorMode();
  const user = useContext(AuthContext);
  const [showModal, setShowModal] = useState<boolean>(false);
  // const [oldScrollY, setOldScrollY] = useState<number>(0);
  // const [currentScrollY, setCurrentScrollY] = useState<number>(0);
  // const [scrollSpeed, setScrollSpeed] = useState<number>(0);
  const [isMobileSticky, setIsMobileSticky] = useState<boolean>(false);
  const [showStickyNav, setShowStickyNav] = useState<boolean>(false);

  const { i18n } = useTranslation();

  const location = useLocation();
  const { pathname } = location;
  // const match = useMatch(pathname);
  // const navigate = useNavigate();

  // Close modal when location changes (navigation occurs)
  useEffect(() => {
    if (showModal) {
      setShowModal(false);
    }
  }, [location.pathname]);

  const handleToggleModal = () => {
    setShowModal(!showModal);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const isHidden =
    (pathname.includes('class') &&
      ((pathname.includes('unit') && !pathname.includes('units')) ||
        pathname.includes('content'))) ||
    (pathname.includes('program') &&
      ((pathname.includes('unit') && !pathname.includes('units')) ||
        pathname.includes('content'))) ||
    pathname.includes('demo-sign-up');

  // const setPassword = useLo

  const signedIn = user?.is_authenticated;
  const settingPassword =
    pathname.includes('/activate/') && pathname.includes('set-password');

  if (isHidden) {
    return null;
  }

  const isMarketplace = ['/info', '/explore', '/experience'].some((a) =>
    pathname.includes(a),
  );

  function onLanguageChange(lng: any) {
    i18n.changeLanguage(lng);
  }

  return (
    <>
      <HeaderContainer className={classNames({ marketplace: isMarketplace })}>
        <ConditionalWrap
          condition={isMarketplace}
          wrap={(c) => <Container maxW={'8xl'}>{c}</Container>}
        >
          <>
            <nav aria-label="Main navigation">
            <NavigationMobileScroll
              className={classnames({
                mobileSticky: isMobileSticky,
                show: showStickyNav,
                marketplace: isMarketplace,
              })}
            >
              <Navigation.Item variant="left" className="logo">
                <ConditionalWrap
                  condition={(signedIn && !settingPassword) || !signedIn}
                  wrap={(c) => <Link to={DashboardPage.getRoute()}>{c}</Link>}
                >
                  {
                    // @ts-expect-error
                    <Logo style={{ width: '92px', filter: colorMode === 'dark' ? 'brightness(0) invert(1)' : undefined }} role="img" aria-label="STEMI" />
                  }
                </ConditionalWrap>
              </Navigation.Item>
              {signedIn && !settingPassword ? (
                <>
                  <Navigation.Item
                    className="hide-phone"
                    variant={'right'}
                    // style={{ marginRight: '48px' }}
                  >
                    <StyledNavLink
                      end
                      to={DashboardPage.getRoute()}
                      onClick={handleCloseModal}
                      style={{ marginLeft: '20px' }}
                    >
                      <Text as="h5" textStyle="h5">
                        {t('navigation.dashboard')}
                      </Text>
                    </StyledNavLink>
                  </Navigation.Item>
                  <Navigation.Item
                    className="hide-phone hide-tablet hide-laptop"
                    variant={'right'}
                    // style={{ marginRight: '48px' }}
                  >
                    <StyledNavA
                      href={`https://editor.stemi.education?token=${user.token}`}
                      onClick={handleCloseModal}
                    >
                      <Text as="h5" textStyle="h5">
                        {t('navigation.editor')}
                      </Text>
                    </StyledNavA>
                  </Navigation.Item>
                  {user.is_demo ? (
                    <Navigation.Item
                      className="hide-phone hide-tablet hide-laptop"
                      variant={'right'}
                      // style={{ marginRight: '48px' }}
                    >
                      <StyledNavLink
                        end
                        to={'https://marketplace.stemi.education/'}
                        onClick={handleCloseModal}
                      >
                        <Text as="h5" textStyle="h5">
                          {t('navigation.allPrograms')}
                        </Text>
                      </StyledNavLink>
                    </Navigation.Item>
                  ) : null}
                  {user['pages']?.licenses ? (
                    <Navigation.Item
                      className="hide-phone"
                      variant={'right'}
                      // style={{ marginRight: '48px' }}
                    >
                      <StyledNavLink
                        to={'/licenses/'}
                        className={classNames({
                          // eslint-disable-next-line no-restricted-globals
                          active: location.pathname.includes(
                            '/unenrolled-students',
                          ),
                        })}
                        onClick={handleCloseModal}
                      >
                        <Text as="h5" textStyle="h5">
                          {t('navigation.manageClasses')}
                        </Text>
                      </StyledNavLink>
                    </Navigation.Item>
                  ) : null}
                  {(user.roles.includes(Role.staff) ||
                    user.roles.includes(Role.organization_user)) && (
                    <Navigation.Item
                      className="hide-phone hide-tablet hide-laptop"
                      variant={'right'}
                    >
                      <StyledNavLink
                        to={'/myprograms'}
                        onClick={handleCloseModal}
                      >
                        <Text as="h5" textStyle="h5">
                          {t('navigation.myPrograms')}
                        </Text>
                      </StyledNavLink>
                    </Navigation.Item>
                  )}
                  {user.flags.includes('new-ai-feature') && !(user.namespaces.length === 1 && user.namespaces.includes('brazil')) && (
                    <Navigation.Item
                      className="hide-phone hide-tablet hide-laptop"
                      variant={'right'}
                    >
                      <StyledNavLink
                        to={'/assistants'}
                        onClick={handleCloseModal}
                      >
                        <Text as="h5" textStyle="h5">
                          {t('navigation.myAssistants')}
                        </Text>
                      </StyledNavLink>
                    </Navigation.Item>
                  )}
                  {(user.roles.includes(Role.staff) ||
                    user.roles.includes(Role.organization_user) ||
                    user.flags.includes('extra-analytics')) && (
                    <Navigation.Item
                      className="hide-phone hide-tablet hide-laptop"
                      variant={'right'}
                      // style={{ marginRight: '48px' }}
                    >
                      <StyledNavLink
                        to={'/staffpanel/'}
                        onClick={handleCloseModal}
                      >
                        <Text as="h5" textStyle="h5">
                          {user.roles.includes(Role.staff)
                            ? t('navigation.staffPanel')
                            : t('navigation.analytics')}
                        </Text>
                      </StyledNavLink>
                    </Navigation.Item>
                  )}
                  {user.roles.includes(Role.staff) && (
                    <Navigation.Item
                      className="hide-phone hide-tablet hide-laptop"
                      variant={'right'}
                      // style={{ marginRight: '48px' }}
                    >
                      <StyledNavA href="/admin" onClick={handleCloseModal}>
                        <Text as="h5" textStyle="h5">
                          {t('navigation.admin')}
                        </Text>
                      </StyledNavA>
                    </Navigation.Item>
                  )}
                  {user.roles.includes(Role.student) && (
                    <Navigation.Item
                      className="hide-phone hide-tablet hide-laptop"
                      variant={'right'}
                      // style={{ marginRight: '48px' }}
                    >
                      <StyledNavLink
                        end
                        to={'/profile'}
                        onClick={handleCloseModal}
                      >
                        <Text as="h5" textStyle="h5">
                          {t('navigation.profile')}
                        </Text>
                      </StyledNavLink>
                    </Navigation.Item>
                  )}
                  {props.showSettings && (
                    <Navigation.Item
                      className="hide-phone hide-tablet hide-laptop"
                      variant={'right'}
                      // style={{ marginRight: '48px' }}
                    >
                      <StyledNavLink
                        end
                        to={'/settings/'}
                        onClick={handleCloseModal}
                      >
                        <Text as="h5" textStyle="h5">
                          {t('navigation.settings')}
                        </Text>
                      </StyledNavLink>
                    </Navigation.Item>
                  )}
                  <Navigation.Item
                    className="hide-phone"
                    variant={'right'}
                    // style={{ marginRight: '48px' }}
                  >
                    <StyledNavLink
                      end
                      to={SignOutPage.getRoute()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTimeout(() => window.history.go(), 0);
                      }}
                    >
                      <Text as="h5" textStyle="h5">
                        {t('navigation.signOut')}
                      </Text>
                    </StyledNavLink>
                  </Navigation.Item>
                </>
              ) : (
                <Navigation.Item
                  variant="right"
                  className="hide-phone hide-tablet hide-laptop"
                  style={{ marginRight: '0px' }}
                >
                  <Link
                    className="nav-link"
                    to={'/join/'}
                    style={{ marginRight: 12 }}
                    onClick={handleCloseModal}
                  >
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      className="secondary navigation"
                      style={{ textAlign: 'center', minWidth: 0 }}
                    >
                      {t('signInPage.join')}
                    </Button>
                  </Link>
                  <Link
                    className="nav-link"
                    to={SignInPage.getRoute()}
                    style={{ marginRight: 12 }}
                    onClick={handleCloseModal}
                  >
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      className="secondary navigation"
                    >
                      {t('signInPage.signIn')}
                    </Button>
                  </Link>
                  <Link
                    className="nav-link"
                    to={SignUpPage.getRoute()}
                    onClick={handleCloseModal}
                  >
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      className="secondary navigation"
                    >
                      {t('signUp.signUp')}
                    </Button>
                  </Link>
                </Navigation.Item>
              )}
              <Navigation.Item
                className="hide-phone hide-tablet hide-laptop"
                variant={'right'}
              >
                <IconButton
                  variant="ghost"
                  aria-label={colorMode === 'dark' ? t('navigation.switchToLight') : t('navigation.switchToDark')}
                  icon={colorMode === 'dark' ? <SunIcon /> : <MoonIcon />}
                  onClick={toggleColorMode}
                  {...focusVisibleProps}
                />
              </Navigation.Item>
              <Navigation.Item
                className="hide-phone hide-tablet hide-laptop"
                variant={'right'}
                // style={{ marginRight: '48px' }}
              >
                <LanguagePicker
                  onChange={onLanguageChange}
                  initialLanguage={i18n.language}
                />
              </Navigation.Item>
              <Navigation.Item
                variant="right"
                className="hide-laptop-desktop"
                style={{ marginRight: '-50px' }}
              >
                <Button variant="unstyled" aria-label="Open navigation menu" onClick={handleToggleModal}>
                  <HamburgerIcon boxSize="24px" />
                </Button>
              </Navigation.Item>
            </NavigationMobileScroll>
            </nav>
            {props.children ? (
              <NavigationChildrenContainer
                className={classNames({
                  marketplace: isMarketplace,
                })}
                children={props.children}
              />
            ) : null}
          </>
        </ConditionalWrap>
      </HeaderContainer>
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        closeOnOverlayClick={true}
        closeOnEsc={true}
        isCentered={false}
        motionPreset="none"
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px)"
          transition="all 0.3s ease-in-out"
        />
        <SlideFade in={showModal}>
          <ModalContent
            onClick={(e) => {
              e.stopPropagation();
              // Check if the clicked element is a link or inside a link
              const target = e.target as HTMLElement;
              const link = target.closest('a') || target.closest('button');
              if (link && link.id !== 'language-picker') {
                handleCloseModal();
              }
            }}
          >
            <MainSidebar />
          </ModalContent>
        </SlideFade>
      </Modal>
    </>
  );
};

export default showSettingsHoc(MainNavigation);
